import { useState } from 'react';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { MODES, SOURCES, VERIFICATION_STEPS } from './constants';

import AddPhoneNumberOrEmail from './components/AddPhoneNumberOrEmail';
import GetVerificationCode from './components/GetVerificationCode';
import VerificationCodeSent from './components/VerificationCodeSent';
import Finished from './components/Finished';

import type { MODES_VALUES, SOURCES_VALUES } from './constants';

type Props = {
  headerText: string;
  initialStep?: number;
  isContentEditable?: boolean;
  initialContentToVerify?: string;
  onContentValueAdded?: (contentValue: string) => Promise<void>;
  onVerificationConfirmation?: (contentValue: string) => Promise<void>;
  showVerificationModal: (value: boolean) => void;
  stepsConfig: Array<string>;
  verificationSuccessfulCTAText?: string;
  verificationMode?: MODES_VALUES;
  verificationSource?: SOURCES_VALUES;
};

const VerificationModal = ({
  headerText,
  initialStep = 0,
  isContentEditable = true,
  initialContentToVerify = '',
  onContentValueAdded,
  onVerificationConfirmation,
  showVerificationModal,
  stepsConfig,
  verificationSuccessfulCTAText = 'Return to listing',
  verificationMode = MODES.PHONE,
  verificationSource = SOURCES.LISTING_CONTACT,
}: Props) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [contentToVerify, setContentToVerify] = useState(initialContentToVerify);
  const [verificationType, setVerificationType] = useState('');

  const incrementStep = (stepToJumpTo?: string) => {
    if (stepToJumpTo) {
      const stepToJumpToIndex = stepsConfig.findIndex((element) => element === stepToJumpTo);
      setCurrentStepIndex(stepToJumpToIndex);
    } else {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && showVerificationModal(false)}
      useDismissProps={{
        outsidePress: false,
      }}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{headerText}</Heading>}
      body={
        <Box
          data-testid="VerificationModal"
          css={{
            marginTop: '0',
            marginInline: 'auto',
            marginBottom: '400',
            maxWidth: '600px',
            textAlign: 'center',
            height: '336px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.ADD_CONTENT && (
            <AddPhoneNumberOrEmail
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              saveContentToVerify={setContentToVerify}
              onContentValueAdded={onContentValueAdded}
              verificationMode={verificationMode}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.GET_VERIFICATION_CODE && (
            <GetVerificationCode
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={contentToVerify}
              saveVerificationType={setVerificationType}
              verificationMode={verificationMode}
              verificationSource={verificationSource}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_CODE_SENT && (
            <VerificationCodeSent
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={contentToVerify}
              verificationType={verificationType}
              onVerificationConfirmation={onVerificationConfirmation}
              verificationMode={verificationMode}
              verificationSource={verificationSource}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_SUCCESS && (
            <Finished
              showVerificationModal={showVerificationModal}
              verificationSuccessfulCTAText={verificationSuccessfulCTAText}
              verificationMode={verificationMode}
            />
          )}
        </Box>
      }
    />
  );
};

export { VerificationModal };
